import { FC, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Box, Paper } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import type { IReferralProgram, IUser } from "@framework/types";
import { CurrencyInput } from "@dzambalaorg/mui-inputs-mask";
import { useUser } from "@dzambalaorg/provider-user";
import { useDeepCompareEffect } from "@dzambalaorg/react-hooks";
import { Button, Tooltip, Typography } from "@dzambalaorg/ui-kit";

export type TReferralProgram = IReferralProgram & {
  id: string;
};

// LEVEL0 = total shares
// ADD LEVEL = default 1%
export const getEmptyProgramLevel = (
  levels: IReferralProgram[],
  merchantId: number,
): Array<{ merchantId: number; level: number; share: number }> => {
  if (levels.length === 0) {
    return [
      {
        merchantId,
        level: 0,
        share: 1000,
      },
      {
        merchantId,
        level: levels.length + 1,
        share: 1000,
      },
    ];
  } else {
    return [
      {
        merchantId,
        level: levels[levels.length - 1].level + 1,
        share: 100,
      },
    ];
  }
};

export const ReferralProgramLevelsInput: FC = () => {
  const { formatMessage } = useIntl();
  const { profile } = useUser<IUser>();
  const merchantId = profile?.merchantId;
  const form = useFormContext<any>();

  const { fields, append, remove } = useFieldArray({ name: "levels", control: form.control });
  const watchFields = useWatch({ name: "levels" });
  const values: TReferralProgram[] = fields.map(
    (field, index) =>
      ({
        ...field,
        ...watchFields[index],
      }) as TReferralProgram,
  );

  const handleOptionAdd = useCallback(
    (): (() => void) => (): void => {
      append(getEmptyProgramLevel(values, merchantId));
    },
    [merchantId, values],
  );

  const handleOptionDelete =
    (i: number): (() => void) =>
    (): void => {
      remove(i);
    };

  useDeepCompareEffect(() => {
    void form.trigger();
  }, [values]);

  return (
    <Box mt={2}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography sx={{ mr: 1 }}>
          <FormattedMessage id="pages.referral.program.levels" />
        </Typography>
        <Tooltip arrow={false} title={formatMessage({ id: "form.tips.create" })}>
          <Button intent="icon" size="small" aria-label="add" onClick={handleOptionAdd()}>
            <Add fontSize="large" color="primary" />
          </Button>
        </Tooltip>
      </Box>

      {values.map((program, i) => (
        <Box key={program.id} mt={1} mb={1} display="flex" justifyContent="space-between" alignItems="center">
          <Box flex={1}>
            <Paper sx={{ p: 2, display: "flex", alignItems: "stretch", flex: 1, flexDirection: "column" }}>
              {i === 0 ? (
                <FormattedMessage id="pages.referral.program.total" />
              ) : (
                <FormattedMessage id="pages.referral.program.level" values={{ level: i }} />
              )}
              <CurrencyInput name={`levels[${i}].share`} symbol="%" />
            </Paper>
          </Box>

          <Box ml={2}>
            <Tooltip title={formatMessage({ id: "form.tips.delete" })}>
              <span>
                <Button intent="icon" aria-label="delete" onClick={handleOptionDelete(i)} disabled={!i}>
                  <Delete />
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>
      ))}
    </Box>
  );
};
